import React from 'react';
import { BADGES, NEXT_ACTIONS } from '../constants/onboardingData';

/**
 * Écran de succès après completion de l'onboarding
 */
export default function SuccessScreen({ formData, onNavigateToAction }) {
  const isStudent = formData?.userType === 'eleve';
  const userName = isStudent ? formData?.studentName : 'parent';

  return (
    <div className="min-h-[100dvh] bg-[#F0FDFA] flex flex-col">
      <div className="flex-1 px-4 py-6 overflow-y-auto">
        {/* Header avec icône */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-yellow-400 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-4xl">✨</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {isStudent 
              ? `Bravo ${userName} !` 
              : 'Bravo, tu as activé le parcours !'
            }
          </h1>
          <p className="text-gray-500">
            {isStudent 
              ? 'Tu es prêt à progresser avec GeniMi !'
              : 'Tu viens d\'offrir à ton enfant une chance de progresser.'
            }
          </p>
          <p className="text-gray-600 mt-2">
            Plus de <span className="text-gray-600 font-semibold">1 200 {isStudent ? 'élèves' : 'parents'} cette semaine</span> ont déjà commencé.
          </p>
        </div>

        {/* Badges Section */}
        <div className="bg-white rounded-2xl p-6 shadow-xl mb-6 border-2 border-emerald-200">
          {/* Badges débloqués */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">🏆</span>
            <span className="font-semibold text-gray-800">Tes badges débloqués</span>
          </div>
          
          <div className="grid grid-cols-2 gap-3 mb-6">
            {BADGES.unlocked.map((badge) => (
              <div
                key={badge.id}
                className={`
                  rounded-xl p-4 text-white
                  ${badge.color === 'green' ? 'bg-gradient-to-br from-emerald-400 to-teal-500' : ''}
                  ${badge.color === 'blue' ? 'bg-gradient-to-br from-blue-400 to-cyan-500' : ''}
                `}
              >
                <span className="text-2xl block mb-2">{badge.icon}</span>
                <p className="font-semibold">{badge.label}</p>
                <p className="text-white/80 text-xs mt-1">{badge.description}</p>
              </div>
            ))}
          </div>

          {/* Badges à débloquer */}
          <p className="font-medium text-gray-600 mb-3">Prochains badges à débloquer :</p>
          <div className="grid grid-cols-2 gap-3">
            {BADGES.locked.map((badge) => (
              <div
                key={badge.id}
                className="bg-gray-50 rounded-xl p-4 border border-gray-200 relative"
              >
                <div className="absolute top-2 right-2 text-gray-400">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-2xl block mb-2 opacity-50">{badge.icon}</span>
                <p className="font-semibold text-gray-700">{badge.label}</p>
                <p className="text-gray-500 text-xs mt-1">{badge.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Prochaine étape recommandée */}
        <p className="font-semibold text-gray-800 mb-3">Prochaine étape recommandée :</p>
        
        <div className="space-y-3">
          {NEXT_ACTIONS.map((action) => (
            <div
              key={action.id}
              onClick={() => onNavigateToAction(action.route)}
              className={`
                rounded-2xl p-4 cursor-pointer transition-all
                ${action.recommended
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
                  : 'bg-white border border-gray-100 hover:border-gray-200 w-full rounded-2xl p-6 shadow-lg'
                }
              `}
            >
              <div className="flex items-center gap-3">
                <div className={`
                  w-12 h-12 rounded-xl flex items-center justify-center shadow-lg
                  ${action.recommended ? 'bg-white/20' : action.id === "aide-devoirs" ? 'bg-gradient-to-br from-teal-400 to-teal-500' : "bg-gradient-to-br from-cyan-400 to-cyan-500"}
                `}>
                  {action.id === 'test-demo' && (
                    <svg className={`w-6 h-6 text-white`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  )}
                  {action.id === 'aide-devoirs' && (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  )}
                  {action.id === 'progres' && (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  )}
                </div>

                <div className="flex-1">
                  <p className={`font-semibold ${action.recommended ? 'text-white' : 'text-gray-800'}`}>
                    {action.label}
                  </p>
                  <p className={`text-sm ${action.recommended ? 'text-white/80' : 'text-gray-500'}`}>
                    {action.description}
                  </p>
                  {action.badge && (
                    <p className={`text-xs mt-1 ${action.recommended ? 'inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full' : 'text-gray-400'}`}>
                      🏆 {action.badge}
                    </p>
                  )}
                </div>

                {action.recommended && (
                  <div className="bg-yellow-400 text-gray-800 text-xs font-semibold px-2 py-1 rounded-full">
                    Recommandé
                  </div>
                )}

                <svg className={`w-5 h-5 ${action.recommended ? 'text-white' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* Info footer */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-2xl p-5 mt-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⏱️</span>
            <div>
              <p className="font-semibold text-gray-800">En 5 minutes seulement</p>
              <p className="text-gray-600 text-sm">
                Le test démo te montre exactement où ton enfant se situe et comment l'aider à progresser.
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Plus de <span className="text-[#2DD4BF] font-semibold">850 parents</span> l'ont fait aujourd'hui.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
